
import React, { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Button, Typography, Spin, Row, Col, message } from "antd";
import { checkShippingItems, apiCreateShippingQueue } from "services/ordersApi";
import { GetDataOrderListItems } from "services/interface";
import { RocketOutlined, CloseOutlined, InfoCircleOutlined } from "@ant-design/icons";

export interface OrderShippingSummaryDetail {
  order_id: string;
  y: number;
  z: number;
}

interface CallShippingModalProps {
  visible: boolean;
  onClose: () => void;
  selectedOrders: GetDataOrderListItems[];
  factoryId: string;
  onSuccess: (ids: string[], details: OrderShippingSummaryDetail[]) => void;
}

const CallShippingModal: FC<CallShippingModalProps> = ({
  visible,
  onClose,
  selectedOrders,
  factoryId,
  onSuccess,
}) => {
  const [step, setStep] = useState<'summary' | 'processing'>('summary');
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState<{ x: number, y: number, z: number, details?: OrderShippingSummaryDetail[] }>({ x: 0, y: 0, z: 0 });

  const navigate = useNavigate();

  useEffect(() => {
    const fetchCounts = async () => {
      setLoading(true);
      try {
        const ids = selectedOrders.map((o: any) => o.order_id || o.id);
        const res = await checkShippingItems(ids);
        if (res.status === 'success') {
          setCounts(res.data);
        }
      } catch (error) {
        console.error(error);
        message.error("ไม่สามารถคำนวณจำนวนรายการได้");
      } finally {
        setLoading(false);
      }
    };

    if (visible && selectedOrders.length > 0) {
      fetchCounts();
      setStep('summary');
    }
  }, [visible, selectedOrders]);

  const handleConfirm = async () => {
    setStep('processing');
    try {
        const ids = selectedOrders.map((o: any) => o.order_id || o.id);
        await apiCreateShippingQueue({ order_ids: ids });
        
        message.success("เชื่อมต่อบริษัทขนส่งเรียบร้อยแล้ว กำลังรอขนส่งดำเนินการ...");
        onSuccess(ids, counts.details || []);
        onClose();
        setTimeout(() => setStep('summary'), 500); // รีเซ็ตสถานะหน้าต่างกลับเป็นหน้าสรุปหลังจาก Animation ปิด Modal ทำงานเสร็จ
    } catch (error) {
        console.error(error);
        message.error("เกิดข้อผิดพลาดในการเชื่อมต่อบริษัทขนส่ง");
        setStep('summary');
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => setStep('summary'), 500);
  };

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      title={null}
      destroyOnClose
      centered
      width={700}
    >
      {step === 'summary' && (
        <div style={{ textAlign: 'center' }}>
            {loading ? <Spin /> : (
            <>
              <Typography.Title level={4} style={{ marginTop:15 }}>รายละเอียด</Typography.Title>
              <div style={{ marginTop:20,marginBottom: 20 ,border: '1px solid #ccc',borderRadius: 10 }}>
                <table 
                style={{ width: '100%',fontSize:'16px',marginTop:'15px',marginBottom:'15px'}}>
                  <tr>
                <td style={{ textAlign: 'right',padding:'3px',width:'45%' }}><b>คำสั่งซื้อทั้งหมด : </b></td>
                <td style={{ textAlign: 'center',padding:'3px',width:'25%',borderBottom:'1px dashed #ccc' }}><b>{counts.x}</b></td>
                <td style={{width:'30%' ,textAlign:'left'}}><b>รายการ</b> </td>
                </tr>
                <tr>
                <td style={{ textAlign: 'right',padding:'3px',width:'45%' }}><b>ส่งผ่าน Partner แบ่งเบา :  </b></td>
                <td style={{ textAlign: 'center',padding:'3px',width:'25%',borderBottom:'1px dashed #ccc' }}><b style={{ color: 'blue' }}>{counts.y}</b></td>
                <td style={{width:'30%' ,textAlign:'left'}}><b>รายการ</b></td>
                </tr>
                <tr>
                <td style={{ textAlign: 'right',padding:'3px',width:'45%' }}><b>ส่งเอง : </b></td>
                <td style={{ textAlign: 'center',padding:'3px',width:'25%',borderBottom:'1px dashed #ccc' }}><b style={{ color: 'green' }}>{counts.z}</b></td>
                <td style={{width:'30%' ,textAlign:'left'}}><b>รายการ</b></td>
                </tr>
                </table>
              </div>
              {counts.y > 0 && (
                <div style={{ marginTop:20, marginBottom: 20, padding: 15, backgroundColor: '#f9efe6ff', border: '1px solid #ffe58f', borderRadius: 10, textAlign: 'left' }}>
                  <Typography.Text style={{ fontSize: '18px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <InfoCircleOutlined style={{ fontSize: '18px', marginTop: '3px' }} />
                    <div>
                      <b>คำเตือน</b> มีรายการสินค้าที่จัดส่งผ่าน Partner แบ่งเบา<br/>
                      โปรดจัดเตรียมสินค้าให้พร้อมสำหรับการจัดส่ง <br/> 
                      บริษัทขนส่งจะเข้ามารับสินค้าของท่านภายใน 1 - 2 วันหลังจาก ออก Label สำเร็จ
                    </div>
                  </Typography.Text>
                </div>
              )}
              <Row gutter={16} justify="center" style={{ marginTop: 20,backgroundColor:'#edededff',padding:'15px' }}>
                <Col>
                  <Button icon={<CloseOutlined />} onClick={handleClose}>
                    ยกเลิก
                  </Button>
                </Col>
                <Col>
                  <Button type="primary" icon={<RocketOutlined />} onClick={handleConfirm} disabled={counts.y === 0 && counts.z === 0}>
                    ยืนยันออก Label
                  </Button>
                </Col>
              </Row>
            </>
            )}
        </div>
      )}

      {step === 'processing' && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <Typography.Title level={5} style={{ marginTop: 20 }}>ระบบกำลังเชื่อมต่อไปยังบริษัทขนส่ง...</Typography.Title>
          <p>กรุณารอสักครู่</p>
        </div>
      )}
    </Modal>
  );
};


export default CallShippingModal;
